import React, { useState } from 'react';
import { HealthBuddy } from '../HealthBuddy';
import { MinusIcon, PlusIcon, CheckIcon, ArrowLeftIcon, MicIcon, ChartIcon, ChevronRightIcon, PillIcon, InhalerIcon, ZapIcon } from '../icons';
import { TINTS, Tint } from '../../data/mock';
import { TRACKERS, Tracker, TRACKING_HISTORY, INHALER_WEEK, HISTORY_STATS } from '../../data/tracking';
/* the asthma + epilepsy medication trackers are combined into one row */
const MEDICATION_ROW = {
  id: 'medication',
  title: 'Medication',
  hint: 'Inhalers & daily meds',
  icon: PillIcon,
  tint: 'coral' as Tint
};
/* picker list: the two medication trackers collapse into MEDICATION_ROW */
const PICKER_ITEMS: {
  id: string;
  title: string;
  hint: string;
  icon: typeof PillIcon;
  tint: Tint;
}[] = (() => {
  const out: {
    id: string;
    title: string;
    hint: string;
    icon: typeof PillIcon;
    tint: Tint;
  }[] = [];
  let medAdded = false;
  for (const t of TRACKERS) {
    if (t.id === 'asthma-medication' || t.id === 'epilepsy-medication') {
      if (!medAdded) {
        out.push(MEDICATION_ROW);
        medAdded = true;
      }
      continue;
    }
    out.push(t);
  }
  return out;
})();
/**
 * Tracking bottom modal — three steps plus the history view:
 *   1. Picker  — which tracker? (8 types) + the "Your history" row
 *   2. Form    — the chosen tracker's quick log
 *   3. Logged  — celebration (the emotional design moment)
 *   H. History — the last two weeks: inhaler chart + every log, day by day
 *
 * Conversational entry skips the picker: "log that I used my inhaler"
 * opens the Asthma Medication form directly (startAt prop) — the assistant
 * already knows which tracker the user means. "How's my tracking going?"
 * routes to the history view the same way (startAt='history').
 */
export function TrackingModal({
  onDone,
  startAt = null
}: {
  onDone?: () => void;
  startAt?: string | null;
}) {
  const [trackerId, setTrackerId] = useState<string | null>(startAt === 'history' ? null : startAt);
  const [showHistory, setShowHistory] = useState(startAt === 'history');
  const [loggedMessage, setLoggedMessage] = useState<string | null>(null);
  const tracker = TRACKERS.find(t => t.id === trackerId);
  const cameFromPicker = startAt === null;
  if (loggedMessage) {
    return <Celebration message={loggedMessage} onDone={onDone} data-id="element-673" />;
  }
  if (showHistory) {
    return <HistoryView onBack={cameFromPicker ? () => setShowHistory(false) : undefined} data-id="element-674" />;
  }
  if (trackerId === 'medication') {
    return <MedicationForm onBack={cameFromPicker ? () => setTrackerId(null) : undefined} onLogged={setLoggedMessage} data-id="element-675" />;
  }
  if (!tracker) {
    return <PickerStep onPick={setTrackerId} onHistory={() => setShowHistory(true)} data-id="element-676" />;
  }
  if (tracker.id === 'asthma-medication') {
    return <InhalerForm onBack={cameFromPicker ? () => setTrackerId(null) : undefined} onLogged={setLoggedMessage} data-id="element-677" />;
  }
  return <QuickLogForm tracker={tracker} onBack={cameFromPicker ? () => setTrackerId(null) : undefined} onLogged={setLoggedMessage} data-id="element-678" />;
}
/* ============================================================
   Step 1 — picker
   ============================================================ */
function PickerStep({
  onPick,
  onHistory
}: {
  onPick: (id: string) => void;
  onHistory: () => void;
}) {
  return <div className="px-5 pb-10 pt-2" data-id="element-679">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-coral-deep" data-id="element-680">
        Tracking
      </p>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-681">
        What would you like to log?
      </h2>

      <div className="mt-5 flex flex-col gap-2.5" data-id="element-682">
        {PICKER_ITEMS.map(t => {
        const tint = TINTS[t.tint];
        const Icon = t.icon;
        return <button key={t.id} onClick={() => onPick(t.id)} className={`flex items-center gap-3.5 rounded-card border ${tint.border} bg-cream p-3.5 text-left shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]`} data-id="element-683">
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint.solid} text-cream shadow-card`} data-id="element-684">
                <Icon size={20} data-id="element-685" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-686">
                <h3 className="font-display text-[15px] font-semibold leading-tight text-ink" data-id="element-687">
                  {t.title}
                </h3>
                <p className={`mt-0.5 text-[12px] font-medium leading-snug ${tint.text}`} data-id="element-688">
                  {t.hint}
                </p>
              </span>
              <ChevronRightIcon size={16} className="shrink-0 text-ink-faint" data-id="element-689" />
            </button>;
      })}
      </div>

      {/* the history row — what all this logging adds up to */}
      <button onClick={onHistory} className="mt-2.5 flex w-full items-center justify-between rounded-card border border-linen-deep bg-cream p-3.5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.97]" data-id="element-690">
        <span className="flex items-center gap-3" data-id="element-691">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-linen text-teal-deep" data-id="element-692">
            <ChartIcon size={20} data-id="element-693" />
          </span>
          <span data-id="element-694">
            <h3 className="font-display text-[14.5px] font-semibold leading-tight text-ink" data-id="element-695">
              Your history
            </h3>
            <p className="mt-0.5 text-[11.5px] font-medium leading-snug text-ink-soft" data-id="element-696">
              35 logs in the last two weeks
            </p>
          </span>
        </span>
        <ChevronRightIcon size={16} className="shrink-0 text-ink-faint" data-id="element-697" />
      </button>

      {/* the voice shortcut — everything in the picker can be said instead */}
      <div className="mt-5 flex items-center gap-3 rounded-card bg-linen p-3.5" data-id="element-698">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal text-cream" data-id="element-699">
          <MicIcon size={16} data-id="element-700" />
        </span>
        <p className="text-[12px] leading-snug text-ink-soft" data-id="element-701">
          Or just tell Sunny —{' '}
          <span className="font-bold text-ink" data-id="element-702">
            “log 2 puffs of my inhaler”
          </span>{' '}
          or{' '}
          <span className="font-bold text-ink" data-id="element-703">
            “I had a seizure this morning”
          </span>
        </p>
      </div>
    </div>;
}
/* ============================================================
   Step 2a — asthma medication (inhaler) form
   ============================================================ */
function InhalerForm({
  onBack,
  onLogged
}: {
  onBack?: () => void;
  onLogged: (msg: string) => void;
}) {
  const [inhalerType, setInhalerType] = useState<'reliever' | 'preventer'>('reliever');
  const [puffs, setPuffs] = useState(2);
  const [when, setWhen] = useState('Just now');
  return <div className="px-5 pb-10 pt-2" data-id="element-704">
      <FormHeader meta="Tracking · Asthma Medication" title="Log inhaler use" onBack={onBack} data-id="element-705" />

      {/* which inhaler */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-706">Which inhaler?</p>
      <div className="mt-2 grid grid-cols-2 gap-2.5" data-id="element-707">
        <button onClick={() => setInhalerType('reliever')} className={`rounded-2xl border-2 p-4 text-left transition-all ${inhalerType === 'reliever' ? 'border-sky bg-sky-soft' : 'border-linen-deep bg-cream'}`} data-id="element-708">
          <div className="h-3 w-8 rounded-full bg-sky" data-id="element-709" />
          <p className="font-display mt-2.5 text-[15px] font-semibold text-ink" data-id="element-710">
            Reliever
          </p>
          <p className="text-[12px] font-medium text-ink-soft" data-id="element-711">The blue one</p>
        </button>
        <button onClick={() => setInhalerType('preventer')} className={`rounded-2xl border-2 p-4 text-left transition-all ${inhalerType === 'preventer' ? 'border-coral bg-coral-soft' : 'border-linen-deep bg-cream'}`} data-id="element-712">
          <div className="h-3 w-8 rounded-full bg-coral" data-id="element-713" />
          <p className="font-display mt-2.5 text-[15px] font-semibold text-ink" data-id="element-714">
            Preventer
          </p>
          <p className="text-[12px] font-medium text-ink-soft" data-id="element-715">The brown one</p>
        </button>
      </div>

      {/* puffs */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-716">
        How many puffs?
      </p>
      <div className="mt-2 flex items-center justify-between rounded-2xl bg-linen p-3" data-id="element-717">
        <button onClick={() => setPuffs(p => Math.max(1, p - 1))} className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-718">
          <MinusIcon size={18} data-id="element-719" />
        </button>
        <span className="font-display text-[32px] font-semibold text-ink" data-id="element-720">
          {puffs}
        </span>
        <button onClick={() => setPuffs(p => Math.min(10, p + 1))} className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-721">
          <PlusIcon size={18} data-id="element-722" />
        </button>
      </div>

      {/* when */}
      <WhenPicker when={when} onChange={setWhen} data-id="element-723" />

      {/* confirm */}
      <button onClick={() => onLogged(`${puffs} ${puffs === 1 ? 'puff' : 'puffs'} of your ${inhalerType}, ${when.toLowerCase()}. That's 5 days in a row of keeping track — nice and steady.`)} className="mt-7 w-full rounded-full bg-coral py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-724">
        Log it
      </button>
      <p className="mt-2.5 text-center text-[11.5px] font-medium text-ink-faint" data-id="element-725">
        You can also say “log 2 puffs of my reliever” any time.
      </p>
    </div>;
}
/* ============================================================
   Step 2a-combined — medication (asthma inhalers + epilepsy daily)
   ============================================================ */
type MedOption = {
  key: string;
  label: string;
  sub: string;
  icon: typeof PillIcon;
  tint: Tint;
  unit: string;
};
const MED_OPTIONS: MedOption[] = [{
  key: 'reliever',
  label: 'Reliever',
  sub: 'Asthma · the blue one',
  icon: InhalerIcon,
  tint: 'sky',
  unit: 'puff'
}, {
  key: 'preventer',
  label: 'Preventer',
  sub: 'Asthma · the brown one',
  icon: InhalerIcon,
  tint: 'coral',
  unit: 'puff'
}, {
  key: 'epilepsy',
  label: 'Epilepsy tablets',
  sub: 'Daily medication',
  icon: ZapIcon,
  tint: 'lilac',
  unit: 'tablet'
}];
function MedicationForm({
  onBack,
  onLogged
}: {
  onBack?: () => void;
  onLogged: (msg: string) => void;
}) {
  const [medKey, setMedKey] = useState('reliever');
  const [count, setCount] = useState(2);
  const [when, setWhen] = useState('Just now');
  const med = MED_OPTIONS.find(m => m.key === medKey)!;
  const tint = TINTS[med.tint];
  const pickMed = (m: MedOption) => {
    setMedKey(m.key);
    setCount(m.key === 'epilepsy' ? 1 : 2);
  };
  const unitLabel = count === 1 ? med.unit : `${med.unit}s`;
  return <div className="px-5 pb-10 pt-2" data-id="element-726">
      <FormHeader meta="Tracking · Medication" title="Log medication" onBack={onBack} data-id="element-727" />

      {/* which medication */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-728">
        Which medication?
      </p>
      <div className="mt-2 flex flex-col gap-2" data-id="element-729">
        {MED_OPTIONS.map(m => {
        const mt = TINTS[m.tint];
        const Icon = m.icon;
        const on = m.key === medKey;
        return <button key={m.key} onClick={() => pickMed(m)} className={`flex items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all ${on ? `${mt.border} ${mt.soft}` : 'border-linen-deep bg-cream'}`} data-id="element-730">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${mt.solid} text-cream shadow-card`} data-id="element-731">
                <Icon size={19} data-id="element-732" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-733">
                <p className="font-display text-[14.5px] font-semibold leading-tight text-ink" data-id="element-734">
                  {m.label}
                </p>
                <p className="text-[11.5px] font-medium text-ink-soft" data-id="element-735">
                  {m.sub}
                </p>
              </span>
              {on && <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${mt.solid} text-cream`} data-id="element-736">
                  <CheckIcon size={13} strokeWidth={2.8} data-id="element-737" />
                </span>}
            </button>;
      })}
      </div>

      {/* how many */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-738">
        How many {med.unit === 'puff' ? 'puffs' : 'tablets'}?
      </p>
      <div className="mt-2 flex items-center justify-between rounded-2xl bg-linen p-3" data-id="element-739">
        <button onClick={() => setCount(c => Math.max(1, c - 1))} className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-740">
          <MinusIcon size={18} data-id="element-741" />
        </button>
        <span className="font-display text-[32px] font-semibold text-ink" data-id="element-742">
          {count}
        </span>
        <button onClick={() => setCount(c => Math.min(10, c + 1))} className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-743">
          <PlusIcon size={18} data-id="element-744" />
        </button>
      </div>

      {/* when */}
      <WhenPicker when={when} onChange={setWhen} data-id="element-745" />

      {/* confirm */}
      <button onClick={() => onLogged(`${count} ${unitLabel} of your ${med.label.toLowerCase()}, ${when.toLowerCase()}. That's another day of keeping on top of it — well done.`)} className={`mt-7 w-full rounded-full ${tint.solid} py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]`} data-id="element-746">
        Log it
      </button>
      <p className="mt-2.5 text-center text-[11.5px] font-medium text-ink-faint" data-id="element-747">
        You can also say “log 2 puffs of my reliever” or “took my epilepsy
        tablets”.
      </p>
    </div>;
}
/* ============================================================
   Step 2b — generic quick log (all other trackers)
   ============================================================ */
function QuickLogForm({
  tracker,
  onBack,
  onLogged
}: {
  tracker: Tracker;
  onBack?: () => void;
  onLogged: (msg: string) => void;
}) {
  const tint = TINTS[tracker.tint];
  const Icon = tracker.icon;
  const [when, setWhen] = useState('Just now');
  const [feeling, setFeeling] = useState<string | null>(null);
  const feelings = ['Good', 'Okay', 'Not great', 'Rough'];
  return <div className="px-5 pb-10 pt-2" data-id="element-748">
      <FormHeader meta={`Tracking · ${tracker.title}`} title={tracker.hint} onBack={onBack} data-id="element-749" />

      {/* tracker identity */}
      <div className={`mt-5 flex items-center gap-3 rounded-card ${tint.soft} p-4`} data-id="element-750">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint.solid} text-cream shadow-card`} data-id="element-751">
          <Icon size={22} data-id="element-752" />
        </span>
        <div data-id="element-753">
          <h3 className="font-display text-[16px] font-semibold text-ink" data-id="element-754">
            {tracker.title}
          </h3>
          <p className={`text-[12px] font-medium ${tint.text}`} data-id="element-755">
            Quick log — details can be added later
          </p>
        </div>
      </div>

      {/* how did it go */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-756">How was it?</p>
      <div className="mt-2 grid grid-cols-4 gap-2" data-id="element-757">
        {feelings.map(f => <button key={f} onClick={() => setFeeling(f)} className={`rounded-2xl px-2 py-3 text-[12.5px] font-bold transition-all ${feeling === f ? `${tint.solid} text-cream shadow-card` : 'bg-linen text-ink-soft hover:bg-linen-deep'}`} data-id="element-758">
            {f}
          </button>)}
      </div>

      {/* when */}
      <WhenPicker when={when} onChange={setWhen} data-id="element-759" />

      {/* note */}
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-760">
        Anything to remember?
      </p>
      <div className="mt-2 rounded-2xl bg-linen p-4" data-id="element-761">
        <p className="text-[13.5px] text-ink-faint" data-id="element-762">
          Add a note, or dictate one…
        </p>
      </div>

      {/* confirm */}
      <button onClick={() => onLogged(`${tracker.title} logged, ${when.toLowerCase()}. Small steps add up — well done for keeping track.`)} className={`mt-7 w-full rounded-full ${tint.solid} py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]`} data-id="element-763">
        Log it
      </button>
      <p className="mt-2.5 text-center text-[11.5px] font-medium text-ink-faint" data-id="element-764">
        You can also just tell Sunny what happened.
      </p>
    </div>;
}
/* ============================================================
   Step 3 — celebration
   ============================================================ */
function Celebration({
  message,
  onDone
}: {
  message: string;
  onDone?: () => void;
}) {
  return <div className="flex flex-col items-center px-5 pb-12 pt-6 text-center" data-id="element-765">
      <HealthBuddy size={96} mood="celebrating" data-id="element-766" />
      <h2 className="font-display mt-5 text-[24px] font-semibold text-ink" data-id="element-767">
        Logged!
      </h2>
      <p className="mt-2 max-w-[270px] text-[14.5px] leading-relaxed text-ink-soft" data-id="element-768">
        {message}
      </p>
      <div className="mt-5 flex items-center gap-1.5" data-id="element-769">
        {['M', 'T', 'W', 'T', 'F'].map((day, i) => <div key={i} className="flex h-9 w-9 items-center justify-center rounded-full bg-sage text-cream" data-id="element-770">
            <CheckIcon size={14} strokeWidth={2.5} data-id="element-771" />
          </div>)}
      </div>
      <button onClick={onDone} className="mt-7 w-full rounded-full bg-teal py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-772">
        Done
      </button>
    </div>;
}
/* ============================================================
   History — the last two weeks (Phase C)
   ============================================================ */
function HistoryView({
  onBack
}: {
  onBack?: () => void;
}) {
  const maxPuffs = Math.max(...INHALER_WEEK.map(d => d.preventer + d.reliever));
  return <div className="px-5 pb-10 pt-2" data-id="element-773">
      <FormHeader meta="Tracking · History" title="Your last two weeks" onBack={onBack} data-id="element-774" />

      {/* headline stats */}
      <div className="mt-5 grid grid-cols-3 gap-2" data-id="element-775">
        {HISTORY_STATS.map(stat => <div key={stat.label} className="rounded-card bg-linen p-3 text-center" data-id="element-776">
            <p className="font-display text-[24px] font-semibold leading-none text-ink" data-id="element-777">
              {stat.value}
            </p>
            <p className="mt-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-soft" data-id="element-778">
              {stat.label}
            </p>
          </div>)}
      </div>

      {/* the inhaler chart — the headline insight */}
      <div className="mt-5 rounded-card bg-linen p-4" data-id="element-779">
        <div className="flex items-baseline justify-between" data-id="element-780">
          <h3 className="font-display text-[15px] font-semibold text-ink" data-id="element-781">
            Inhaler use
          </h3>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-faint" data-id="element-782">
            Last 7 days
          </p>
        </div>

        {/* bars — preventer is the steady base, the reliever only shows up when it was needed */}
        <div className="mt-4 flex items-end justify-between gap-1.5" data-id="element-783">
          {INHALER_WEEK.map((d, i) => {
          const isToday = i === INHALER_WEEK.length - 1;
          return <div key={d.day} className="flex flex-1 flex-col items-center gap-1.5" data-id="element-784">
                <div className="flex h-[88px] w-full flex-col items-center justify-end gap-[3px]" data-id="element-785">
                  {d.reliever > 0 && <div className="w-4 rounded-full bg-sky" style={{
                height: Math.max(8, d.reliever / maxPuffs * 84)
              }} data-id="element-786" />}
                  {d.preventer > 0 && <div className="w-4 rounded-full bg-coral" style={{
                height: Math.max(8, d.preventer / maxPuffs * 84)
              }} data-id="element-787" />}
                </div>
                <p className={`text-[10px] font-bold ${isToday ? 'text-ink' : 'text-ink-faint'}`} data-id="element-788">
                  {d.day}
                </p>
              </div>;
        })}
        </div>

        {/* legend */}
        <div className="mt-3 flex items-center gap-4" data-id="element-789">
          <span className="flex items-center gap-1.5 text-[11px] font-bold text-ink-soft" data-id="element-790">
            <span className="h-2 w-2 rounded-full bg-coral" data-id="element-791" /> Preventer
          </span>
          <span className="flex items-center gap-1.5 text-[11px] font-bold text-ink-soft" data-id="element-792">
            <span className="h-2 w-2 rounded-full bg-sky" data-id="element-793" /> Reliever
          </span>
        </div>

        {/* the supportive read of the data — never clinical, never alarming */}
        <p className="mt-3 rounded-2xl bg-cream p-3 text-[12px] leading-relaxed text-ink-soft" data-id="element-794">
          Your preventer routine has been steady all week, and you've only
          needed the reliever around Friday's flare-up. That's exactly the
          pattern your care plan aims for.
        </p>
      </div>

      {/* every log, day by day */}
      <h3 className="font-display mt-7 text-[17px] font-semibold text-ink" data-id="element-795">
        Everything you've logged
      </h3>
      <div className="mt-3 flex flex-col gap-5" data-id="element-796">
        {TRACKING_HISTORY.map(day => <div key={day.label} data-id="element-797">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-faint" data-id="element-798">
              {day.label}
            </p>
            <div className="mt-2 flex flex-col gap-2" data-id="element-799">
              {day.entries.map((entry, i) => {
            const tracker = TRACKERS.find(t => t.id === entry.trackerId);
            if (!tracker) return null;
            const tint = TINTS[tracker.tint];
            const Icon = tracker.icon;
            return <div key={i} className="flex items-center gap-3 rounded-card bg-linen p-3" data-id="element-800">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tint.solid} text-cream`} data-id="element-801">
                      <Icon size={18} data-id="element-802" />
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-803">
                      <h4 className="font-display text-[14px] font-semibold leading-tight text-ink" data-id="element-804">
                        {tracker.title}
                      </h4>
                      <p className="mt-0.5 truncate text-[12px] font-medium leading-snug text-ink-soft" data-id="element-805">
                        {entry.detail}
                      </p>
                    </div>
                    <p className="shrink-0 text-[11px] font-bold text-ink-faint" data-id="element-806">
                      {entry.time}
                    </p>
                  </div>;
          })}
            </div>
          </div>)}
      </div>

      {/* the voice shortcut — history is also askable */}
      <div className="mt-6 flex items-center gap-3 rounded-card bg-linen p-3.5" data-id="element-807">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal text-cream" data-id="element-808">
          <MicIcon size={16} data-id="element-809" />
        </span>
        <p className="text-[12px] leading-snug text-ink-soft" data-id="element-810">
          Or just ask Sunny —{' '}
          <span className="font-bold text-ink" data-id="element-811">“how's my tracking going?”</span>{' '}
          or{' '}
          <span className="font-bold text-ink" data-id="element-812">
            “when did I last use my reliever?”
          </span>
        </p>
      </div>
    </div>;
}
/* ============================================================
   shared bits
   ============================================================ */
function FormHeader({
  meta,
  title,
  onBack
}: {
  meta: string;
  title: string;
  onBack?: () => void;
}) {
  return <>
      <div className="flex items-center gap-2" data-id="element-813">
        {onBack && <button onClick={onBack} className="flex h-8 w-8 items-center justify-center rounded-full bg-linen text-ink-soft transition-transform active:scale-90" data-id="element-814">
            <ArrowLeftIcon size={15} data-id="element-815" />
          </button>}
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-coral-deep" data-id="element-816">
          {meta}
        </p>
      </div>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-817">
        {title}
      </h2>
    </>;
}
function WhenPicker({
  when,
  onChange
}: {
  when: string;
  onChange: (w: string) => void;
}) {
  return <>
      <p className="mt-5 text-[13px] font-bold text-ink-soft" data-id="element-818">When?</p>
      <div className="mt-2 flex gap-2" data-id="element-819">
        {['Just now', 'This morning', 'Earlier'].map(option => <button key={option} onClick={() => onChange(option)} className={`flex-1 rounded-full px-3 py-2.5 text-[12.5px] font-bold transition-all ${when === option ? 'bg-teal text-cream' : 'bg-linen text-ink-soft hover:bg-linen-deep'}`} data-id="element-820">
            {option}
          </button>)}
      </div>
    </>;
}